import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight, Package, Mail, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, failed, expired
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(response.data);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (pollCount < 5) {
          // Continue polling
          setPollCount(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          // Max polls reached, show pending status
          setStatus('pending');
          setPaymentDetails(response.data);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount < 3) {
          setPollCount(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-36 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {status === 'loading' && (
            <div className="bg-white rounded-3xl shadow-xl p-12 border-2 border-gray-100 text-center">
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-bold text-3xl mb-4">Verifying Payment...</h1>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white rounded-3xl shadow-xl p-12 border-2 border-green-200 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="font-bold text-3xl mb-4 text-green-800">Payment Successful!</h1>
              <p className="text-gray-600 mb-8">
                Thank you for your order. Your payment has been processed successfully.
              </p>

              {paymentDetails && (
                <div className="bg-gray-50 rounded-2xl p-6 mb-8 text-left">
                  <h3 className="font-semibold text-lg mb-4">Order Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Type:</span>
                      <span className="font-semibold">
                        {paymentDetails.transaction?.order_type === 'fcl_deposit' 
                          ? '10% Deposit (Shared Container)' 
                          : 'Full Payment (Direct Ship)'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-semibold text-green-600">
                        ${paymentDetails.transaction?.total_amount?.toFixed(2) || (paymentDetails.amount_total / 100).toFixed(2)}
                      </span>
                    </div>
                    {paymentDetails.transaction?.service_fee > 0 && (
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Includes Service Fee:</span>
                        <span>${paymentDetails.transaction.service_fee.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 rounded-2xl p-6 mb-8">
                <div className="flex items-center justify-center mb-3">
                  <Mail className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="font-semibold text-blue-800">Check Your Email</span>
                </div>
                <p className="text-sm text-blue-700">
                  We've sent a confirmation email with your order details and next steps.
                </p>
              </div>

              {paymentDetails?.transaction?.order_type === 'fcl_deposit' && (
                <div className="bg-yellow-50 rounded-2xl p-6 mb-8 text-left">
                  <div className="flex items-start">
                    <Package className="w-6 h-6 text-yellow-600 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-yellow-800 mb-2">What Happens Next?</h4>
                      <ul className="text-sm text-yellow-700 space-y-2">
                        <li>• Your 30% deposit secures your spot in the shared container</li>
                        <li>• Production starts when container reaches 80% capacity</li>
                        <li>• 70% balance due upon production completion</li>
                        <li>• Estimated delivery: 45-60 days after production begins</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {paymentDetails?.transaction?.order_type === 'lcl_full' && (
                <div className="bg-orange-50 rounded-2xl p-6 mb-8 text-left">
                  <div className="flex items-start">
                    <Package className="w-6 h-6 text-orange-600 mr-3 mt-1" />
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2">What Happens Next?</h4>
                      <ul className="text-sm text-orange-700 space-y-2">
                        <li>• Your order will ship within 3-5 business days</li>
                        <li>• You'll receive tracking information via email</li>
                        <li>• Estimated delivery: ~30 days</li>
                        <li>• Priority handling throughout the process</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Create Account CTA */}
              <div className="bg-gradient-to-r from-primary/10 to-blue-100 rounded-2xl p-6 mb-8 border border-primary/20">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mr-4">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800 mb-2">Track Your Order Progress</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Create a free account to see real-time container status, production updates, and manage all your orders in one place.
                    </p>
                    <Link to="/my-orders">
                      <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-account-cta">
                        <User className="w-4 h-4 mr-2" /> Create Account & Track Order
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/products')}
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="continue-shopping-btn"
                >
                  Continue Shopping <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="rounded-full"
                  data-testid="go-home-btn"
                >
                  Go to Home
                </Button>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="bg-white rounded-3xl shadow-xl p-12 border-2 border-yellow-200 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-6">
                <Loader2 className="w-12 h-12 text-yellow-600" />
              </div>
              <h1 className="font-bold text-3xl mb-4 text-yellow-800">Payment Processing</h1>
              <p className="text-gray-600 mb-8">
                Your payment is being processed. This may take a few minutes.
                Please check your email for confirmation.
              </p>
              <Button
                onClick={() => navigate('/products')}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Continue Shopping <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === 'expired' && (
            <div className="bg-white rounded-3xl shadow-xl p-12 border-2 border-red-200 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="font-bold text-3xl mb-4 text-red-800">Session Expired</h1>
              <p className="text-gray-600 mb-8">
                Your payment session has expired. Please try again.
              </p>
              <Button
                onClick={() => navigate('/products')}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Start Over <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {status === 'failed' && (
            <div className="bg-white rounded-3xl shadow-xl p-12 border-2 border-red-200 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
              <h1 className="font-bold text-3xl mb-4 text-red-800">Payment Issue</h1>
              <p className="text-gray-600 mb-8">
                We couldn't verify your payment. If you believe this is an error,
                please contact support or check your email for confirmation.
              </p>
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={() => navigate('/products')}
                  className="rounded-full bg-primary hover:bg-primary/90"
                >
                  Try Again <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  variant="outline"
                  className="rounded-full"
                >
                  Contact Support
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
